import { EntityAdapter, EntityState } from '@ngrx/entity';
import { DateTime } from 'luxon';
import * as fromGenerated from '../../_generated';

export interface ActiveRenameRequest {
  requestId: string;
  /** Captured at dispatch so a failed rename can undo the optimistic bump. */
  previousUpdatedAt: string;
}

export interface RenameRequestState {
  activeRenameRequests: Record<number, ActiveRenameRequest>;
}

interface ChatEntity {
  id: number;
  title?: string;
  updatedAt: string;
}

export const renameChatInState = <
  T extends ChatEntity,
  S extends EntityState<T>,
>(
  adapter: EntityAdapter<T>,
  state: S,
  id: number,
  title: string,
  updatedAt?: string,
): S => {
  if (!state.entities[id]) {
    return state;
  }

  return adapter.updateOne(
    {
      id,
      changes: { title, ...(updatedAt ? { updatedAt } : {}) } as Partial<T>,
    },
    state,
  );
};

/**
 * Optimistically applies the rename. `renamedAt` also bumps `updatedAt` so the
 * chat moves to the top of the list immediately — matching the server, which
 * bumps `updatedAt` on rename (otherwise the reorder only appears on reload).
 */
export const setRenameRequest = <
  T extends ChatEntity,
  S extends EntityState<T> & RenameRequestState,
>(
  adapter: EntityAdapter<T>,
  state: S,
  id: number,
  title: string,
  requestId: string,
  renamedAt: string,
): S => {
  const previous = state.entities[id];
  if (!previous) {
    return state;
  }

  const renamedState = renameChatInState(adapter, state, id, title, renamedAt);

  return {
    ...renamedState,
    activeRenameRequests: {
      ...state.activeRenameRequests,
      [id]: { requestId, previousUpdatedAt: previous.updatedAt },
    },
  };
};

export const isRenameRequestCurrent = (
  activeRenameRequests: Record<number, ActiveRenameRequest>,
  id: number,
  requestId: string,
): boolean => activeRenameRequests[id]?.requestId === requestId;

/**
 * Success handler: re-asserts the optimistic title (idempotent) so a socket
 * payload that slipped in between dispatch and success cannot leave a stale
 * title behind, then releases the request slot. Stale successes are ignored.
 */
export const confirmRenameIfCurrent = <
  T extends ChatEntity,
  S extends EntityState<T> & RenameRequestState,
>(
  adapter: EntityAdapter<T>,
  state: S,
  id: number,
  title: string,
  requestId: string,
): S => {
  if (!isRenameRequestCurrent(state.activeRenameRequests, id, requestId)) {
    return state;
  }

  const renamedState = renameChatInState(adapter, state, id, title);
  const { [id]: _removedRequest, ...activeRenameRequests } =
    state.activeRenameRequests;
  return { ...renamedState, activeRenameRequests };
};

export const revertRenameIfCurrent = <
  T extends ChatEntity,
  S extends EntityState<T> & RenameRequestState,
>(
  adapter: EntityAdapter<T>,
  state: S,
  id: number,
  previousTitle: string,
  requestId: string,
): S => {
  const activeRequest = state.activeRenameRequests[id];
  if (activeRequest?.requestId !== requestId) {
    return state;
  }

  const renamedState = renameChatInState(
    adapter,
    state,
    id,
    previousTitle,
    activeRequest.previousUpdatedAt,
  );
  const { [id]: _removedRequest, ...activeRenameRequests } =
    state.activeRenameRequests;
  return { ...renamedState, activeRenameRequests };
};

/**
 * A socket answer payload may have been built server-side before an in-flight
 * rename committed; while a rename is pending, the optimistic title owns the
 * store and the payload's title is dropped.
 */
export const omitTitleWhileRenamePending = <C extends { title?: string }>(
  state: RenameRequestState,
  id: number,
  changes: C,
): Partial<C> => {
  if (!state.activeRenameRequests[id]) {
    return changes;
  }

  const { title: _title, ...rest } = changes;
  // Safe: `rest` is `changes` minus `title`, but TS cannot prove
  // Omit<C, 'title'> ⊂ Partial<C> for an unbound type parameter.
  return rest as Partial<C>;
};

/**
 * Socket payloads can be built before an optimistic rename bumped
 * `updatedAt`; applying the older timestamp would move the chat back down
 * the list. Activity timestamps are monotonic — keep the newest.
 */
export const clampUpdatedAtToNewest = <C extends { updatedAt?: string }>(
  currentUpdatedAt: string | undefined,
  changes: C,
): C => {
  if (
    !changes.updatedAt ||
    !currentUpdatedAt ||
    DateTime.fromISO(changes.updatedAt).toMillis() >=
      DateTime.fromISO(currentUpdatedAt).toMillis()
  ) {
    return changes;
  }

  return { ...changes, updatedAt: currentUpdatedAt };
};

/**
 * Sorts messages by createdAt ascending.
 * Store invariant: messages are always in chronological order.
 */
export const sortMessagesByCreatedAt = (
  messages: fromGenerated.ChatMessageView[],
): fromGenerated.ChatMessageView[] => {
  return [...messages].sort(
    (a, b) =>
      DateTime.fromISO(a.createdAt).toMillis() -
      DateTime.fromISO(b.createdAt).toMillis(),
  );
};

/**
 * Sort comparer for chats by updatedAt descending (newest first).
 * Used with createEntityAdapter to keep the entities list ordered.
 */
export const sortChatsByUpdatedAtComparer = (
  a: { updatedAt: string },
  b: { updatedAt: string },
): number =>
  DateTime.fromISO(b.updatedAt).toMillis() -
  DateTime.fromISO(a.updatedAt).toMillis();
